import React from 'react';
import { FixedElementBadge } from '../FixedElementBadge';
import useWindowSize from '../../../hooks/use-window-size';

const NARROW_MOBILE_BREAKPOINT = 440;

export interface MeasurementsMobileGridItem {
  id: string;
  label: string;
  value: string;
  isFixed?: boolean;
}

interface MeasurementsMobileGridProps {
  items: MeasurementsMobileGridItem[];
  selectedItemId?: string | null;
  onItemClick?: (id: string) => void;
  onItemHover?: (id: string | null) => void;
}

/**
 * Uniwersalny komponent siatki dla mobile
 * Dynamicznie układa elementy w grid z odpowiednimi borderami
 * - dla 1-3 elementów: 1 wiersz
 * - dla 4 elementów: 2x2
 * - dla 5 elementów: 3 + 2
 * - dla 6 elementów: 3 + 3
 * - dla 7 elementów: 4 + 3 (poniżej 440px: 3 + 3 + 1)
 * - dla 8 elementów: 4 + 4 (poniżej 440px: 3 + 3 + 2)
 * - dla 9 elementów: 3 + 3 + 3
 */
export const MeasurementsMobileGrid: React.FC<MeasurementsMobileGridProps> = ({ items, selectedItemId, onItemClick, onItemHover }) => {
  const count = items.length;
  const { windowSize } = useWindowSize();
  const isNarrowMobile = typeof windowSize.width === 'number' && windowSize.width < NARROW_MOBILE_BREAKPOINT;

  const getGridLayout = (count: number): { cols: number; rows: number[][] } => {
    // Dla wąskich ekranów (<440px) kolumny są zawężone do 3, żeby 7/8 elementów mieściło się w siatce
    if (isNarrowMobile) {
      switch (count) {
        case 7:
          return { cols: 3, rows: [[0, 1, 2], [3, 4, 5], [6]] };
        case 8:
          return { cols: 3, rows: [[0, 1, 2], [3, 4, 5], [6, 7]] };
      }
    }

    switch (count) {
      case 1:
        return { cols: 1, rows: [[0]] };
      case 2:
        return { cols: 2, rows: [[0, 1]] };
      case 3:
        return { cols: 3, rows: [[0, 1, 2]] };
      case 4:
        return { cols: 2, rows: [[0, 1, 2], [3]] };
      case 5:
        return { cols: 3, rows: [[0, 1, 2], [3, 4]] };
      case 6:
        return { cols: 3, rows: [[0, 1, 2], [3, 4, 5]] };
      case 7:
        return { cols: 4, rows: [[0, 1, 2, 3], [4, 5, 6]] };
      case 8:
        return { cols: 4, rows: [[0, 1, 2, 3], [4, 5, 6, 7]] };
      case 9:
        return { cols: 3, rows: [[0, 1, 2], [3, 4, 5], [6, 7, 8]] };
      default: {
        const cols = Math.min(4, Math.ceil(Math.sqrt(count)));
        const rowsData: number[][] = [];
        for (let i = 0; i < count; i += cols) {
          rowsData.push(items.slice(i, Math.min(i + cols, count)).map((_, idx) => i + idx));
        }
        return { cols, rows: rowsData };
      }
    }
  };

  const layout = getGridLayout(count);
  const borderColor = '#353a6b';

  return (
    <div className='flex relative z-20 px-2'>

      <div className="bg-navy-900 rounded-[15px] mx-auto">
        {layout.rows.map((rowIndices, rowIndex) => {
          const isLastRow = rowIndex === layout.rows.length - 1;
          const itemsInRow = rowIndices.length;

          return (
            <div
              key={rowIndex}
              className="flex"
              style={{
                borderBottom: !isLastRow ? `1px solid ${borderColor}` : undefined,
              }}
            >
              {rowIndices.map((itemIndex, colIndex) => {
                const item = items[itemIndex];
                const isFirstInRow = colIndex === 0;
                const isLastInRow = colIndex === itemsInRow - 1;
                const isFirstRow = rowIndex === 0;
                const isLastRowVisual = isLastRow;

                const cornerClass = [
                  isFirstRow && isFirstInRow && 'rounded-tl-[15px]',
                  isFirstRow && isLastInRow && 'rounded-tr-[15px]',
                  isLastRowVisual && isFirstInRow && 'rounded-bl-[15px]',
                  isLastRowVisual && isLastInRow && 'rounded-br-[15px]',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <div
                    key={item.id}
                    className={`relative flex flex-col gap-2.5 items-center text-center px-4 py-4 flex-1 ${onItemClick ? 'cursor-pointer' : ''} ${selectedItemId === item.id ? 'bg-blue-900/30' : ''} ${cornerClass} transition-colors`}
                    style={{
                      borderRight: !isLastInRow ? `1px solid ${borderColor}` : undefined,
                      minWidth: '80px',
                    }}
                    onClick={onItemClick ? () => onItemClick(item.id) : undefined}
                    onMouseEnter={onItemHover ? () => onItemHover(item.id) : undefined}
                    onMouseLeave={onItemHover ? () => onItemHover(null) : undefined}
                  >
                    {item.isFixed && (
                      <div className="absolute top-[-16px] lg:top-[-20px] left-1/2 z-10 -translate-x-1/2">
                        <FixedElementBadge disclaimerText="Element naprawiany" />
                      </div>
                    )}
                    <div className="text-blue-300 text-base font-medium leading-[14px]">
                      {item.label}
                    </div>
                    <div className="text-white text-[13px] font-semibold leading-[14px] whitespace-nowrap">
                      {item.value}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
